import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Capacitor } from '@capacitor/core';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {

    constructor(private router: Router) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        // ── Member portal requests use a separate JWT key ──────────────────────
        const isMemberPortalUrl = request.url.includes('/member_portal/')
            || (request.url.includes('/notif/') && request.url.includes('_member'));
        const token = isMemberPortalUrl
            ? localStorage.getItem('milko_member_jwt')
            : localStorage.getItem('milko_jwt');

        const headers: Record<string, string> = {
            'Accept': 'application/json',
            'x-master-lock': environment.masterLock,
            'x-master-key': environment.masterKey,
            'x-platform': environment.isPlayStoreBuild
                ? 'PLAY_STORE'
                : (Capacitor.isNativePlatform() ? 'ANDROID' : 'WEB'),
        };

        // Do NOT set Content-Type for FormData — browser must set it with the multipart boundary
        if (!(request.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        // Attach JWT on every request if available (skip login/signup endpoints)
        const isAuthEndpoint = request.url.includes('app_login.php')
            || request.url.includes('create_milk_users.php')
            || request.url.includes('send_sms.php')
            || request.url.includes('verify_otp.php')
            || request.url.includes('member_portal/send_otp.php')
            || request.url.includes('member_portal/verify_login.php');

        if (token && !isAuthEndpoint) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Use responseType: 'text' so we can strip UTF-8 BOM (\uFEFF / 0xEF BB BF)
        // that some PHP files prepend, which causes JSON.parse to fail.
        const apiReq = (request.clone({ setHeaders: headers, responseType: 'text' }) as unknown) as HttpRequest<unknown>;

        return next.handle(apiReq).pipe(
            map((event: HttpEvent<unknown>) => {
                if (event instanceof HttpResponse) {
                    let raw = (event.body as string) || '';
                    // Strip one or more BOM characters from the start
                    while (raw.charCodeAt(0) === 0xFEFF) { raw = raw.slice(1); }
                    try {
                        return event.clone({ body: JSON.parse(raw) });
                    } catch {
                        return event.clone({ body: raw });
                    }
                }
                return event;
            }),
            catchError((error: HttpErrorResponse) => {
                // Parse error body — PHP may send JSON as text/html or BOM-prefixed text
                let errorBody = error.error;
                if (typeof errorBody === 'string') {
                    let raw = errorBody;
                    while (raw.charCodeAt(0) === 0xFEFF) { raw = raw.slice(1); }
                    try { errorBody = JSON.parse(raw); } catch { /* not JSON, keep as-is */ }
                }

                // ── Member portal 401 — redirect to member login ──────────────────
                const isMemberUrl = (error.url || '').includes('/member_portal/')
                    || ((error.url || '').includes('/notif/') && (error.url || '').includes('_member'));
                if (error.status === 401 && isMemberUrl) {
                    const wasMemberLoggedIn = !!localStorage.getItem('milko_member_jwt');
                    localStorage.removeItem('milko_member_jwt');
                    localStorage.removeItem('milko_member_user');
                    if (wasMemberLoggedIn) {
                        this.router.navigate(['/member-portal/login'], {
                            queryParams: { reason: 'session_expired' }
                        });
                    }
                    return throwError(() => ({
                        status: error.status, error: error.error, errorBody,
                        userMessage: 'Your session has expired. Please log in again.'
                    }));
                }

                // JWT expired or invalid — clear session and redirect to login
                if (error.status === 401 && errorBody?.jwt_error) {
                    const wasLoggedIn = !!localStorage.getItem('milko_user');
                    localStorage.removeItem('milko_jwt');
                    localStorage.removeItem('milko_user');
                    localStorage.removeItem('milko_ledger_cfg');
                    localStorage.removeItem('milko_login_raw');
                    if (wasLoggedIn) {
                        this.router.navigate(['/auth/login'], {
                            queryParams: { reason: 'session_expired' }
                        });
                    }
                    return throwError(() => ({
                        status: error.status, error: error.error, errorBody,
                        userMessage: 'Your session has expired. Please log in again.'
                    }));
                }

                // Account deactivated by dairy admin — PHP returns user_inactive flag
                if ((error.status === 401 || error.status === 403) &&
                    (errorBody?.user_inactive || errorBody?.account_deactivated || errorBody?.is_inactive)) {
                    const wasLoggedIn = !!localStorage.getItem('milko_user');
                    ['milko_jwt', 'milko_user', 'milko_ledger_cfg', 'milko_login_raw']
                        .forEach(k => localStorage.removeItem(k));
                    if (wasLoggedIn) {
                        this.router.navigate(['/auth/login'], {
                            queryParams: { reason: 'account_deactivated' }
                        });
                    }
                    return throwError(() => ({
                        status: error.status, error: error.error, errorBody,
                        userMessage: 'Your account has been deactivated. Please contact your dairy admin.'
                    }));
                }

                // 401 from a non-auth endpoint without jwt_error means the server
                // rejected the session (e.g. JWT blacklisted, user deleted on server).
                // Treat as session expired so the client doesn't stay stuck.
                const isAuthUrl = (error.url || '').includes('app_login.php')
                    || (error.url || '').includes('create_milk_users.php')
                    || (error.url || '').includes('send_sms.php')
                    || (error.url || '').includes('verify_otp.php')
                    || (error.url || '').includes('change_password.php')
                    || (error.url || '').includes('delete_account.php');
                if (error.status === 401 && !isAuthUrl) {
                    const wasLoggedIn = !!localStorage.getItem('milko_user');
                    ['milko_jwt', 'milko_user', 'milko_ledger_cfg', 'milko_login_raw']
                        .forEach(k => localStorage.removeItem(k));
                    if (wasLoggedIn) {
                        this.router.navigate(['/auth/login'], {
                            queryParams: { reason: 'session_expired' }
                        });
                    }
                    return throwError(() => ({
                        status: error.status, error: error.error, errorBody,
                        userMessage: errorBody?.message || 'Your session has expired. Please log in again.'
                    }));
                }

                // Determine a human-readable message based on status and body
                let userMessage: string;
                if (error.status === 0) {
                    userMessage = 'Unable to connect to the server. Please check your internet connection.';
                } else if (error.status === 401) {
                    userMessage = errorBody?.message || 'Invalid username or password.';
                } else if (error.status === 403) {
                    // PHP sends web_restricted:true for BASIC plan web-access blocks
                    if (errorBody?.web_restricted) {
                        userMessage = 'Web access is not available on the BASIC plan. Please use the mobile app or upgrade your plan.';
                    } else {
                        userMessage = errorBody?.message || 'Access denied.';
                    }
                } else if (error.status === 429) {
                    userMessage = errorBody?.message || 'Too many attempts. Please try again later.';
                } else if (error.status >= 500) {
                    userMessage = 'Server error. Please try again later.';
                } else {
                    userMessage = errorBody?.message || 'An unexpected error occurred. Please try again.';
                }

                // Use explicit property extraction (not spread) to guarantee status is always present
                // Set error = errorBody (parsed) so all consumers using err.error?.xxx keep working
                return throwError(() => ({
                    status: error.status,
                    statusText: error.statusText,
                    url: error.url,
                    error: errorBody,
                    errorBody,
                    userMessage
                }));
            })
        );
    }
}
